import { IComponentOptions, IOnChangesObjectOf, IPromise, ITimeoutService, noop } from "angular";
import { v4 as uuidv4 } from "uuid";
import { GtmhubController } from "@gtmhub/core";
import { INoteState, IHeadingState, IWhiteboardShapeState, IPosition, createImageRequestPayload, BasicShapeType } from "../rendering/shapes";
import { GoalModalsService } from "@gtmhub/goals/goalModalsService";
import { FontDownloader } from "../rendering/font-downloader";
import { ShapeStateConverter } from "../rendering/state/shape-state-converter";
import { viewportMargin } from "../rendering/whiteboard-renderer";
import { WhiteboardModalService } from "../whiteboard-modal-service";
import { IImageState, IOriginalImageProps } from "../rendering/shapes/image";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { WhiteboardImageService } from "../whiteboard-image-service";
import { localize } from "@gtmhub/localization";
import { WhiteboardStateHolder } from "../rendering/state/whiteboard-state-holder";
import { createBasicShape, createNewHeading, createNewImage, createNewNote } from "./utils";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { getCurrentUserId } from "@gtmhub/users";
import { IReactionSettings } from "../models";
import { IAllowedActionsMap } from "@gtmhub/sessions";
import { handleOutsideMouseEvent } from "@gtmhub/shared/directives/close-on-mousedown";
import { IBasicShapeState } from "@gtmhub/whiteboards/rendering/shapes/basic-shape";

const defaultHeadingFontFamily = "Kalam";

export interface IWhiteboardStateParams {
  whiteboardId: string;
}

export interface IWhiteboardMenuBindings {
  reactionSettings: IReactionSettings;
  currentUserAllowedActions: (keyof IAllowedActionsMap)[];
  viewport(): IPosition;
  shapeCreate(args: { shape: IWhiteboardShapeState }): void;
  getDefaultSessionId(): IPromise<string>;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface WhiteboardMenuComponentCtrl extends IWhiteboardMenuBindings {}

export class WhiteboardMenuComponentCtrl extends GtmhubController {
  private currentWhiteboardId: string;

  showReactionSettings: boolean;
  currentUserAllowedActionsMap: IAllowedActionsMap;

  static $inject = [
    "FontDownloader",
    "GoalModalsService",
    "WhiteboardModalService",
    "ShapeStateConverter",
    "UIErrorHandlingService",
    "$stateParams",
    "WhiteboardImageService",
    "WhiteboardStateHolder",
    "$rootScope",
    "AnalyticsService",
    "$timeout",
  ];

  constructor(
    fontDownloader: FontDownloader,
    private goalModalsService: GoalModalsService,
    private whiteboardModalService: WhiteboardModalService,
    private shapeStateConverter: ShapeStateConverter,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $stateParams: IWhiteboardStateParams,
    private whiteboardImageService: WhiteboardImageService,
    private whiteboardStateHolder: WhiteboardStateHolder,
    private $rootScope: ITraceRootScopeService,
    private analyticsService: AnalyticsService,
    private $timeout: ITimeoutService
  ) {
    super();

    this.currentWhiteboardId = this.$stateParams.whiteboardId;
    this.showReactionSettings = false;
    // start loading the default font and hope it will be loaded before it is used,
    // otherwise the browser will fallback to some other font
    fontDownloader.loadFonts(defaultHeadingFontFamily).catch(noop);
  }

  $onInit() {
    this.onDestroy(
      handleOutsideMouseEvent("mousedown", () => this.$timeout(() => (this.showReactionSettings = false)), "whiteboard-reaction-settings", "#reaction-settings-icon")
    );
  }

  $onChanges(changesObj: IOnChangesObjectOf<IWhiteboardMenuBindings>): void {
    if (changesObj.currentUserAllowedActions) {
      this.currentUserAllowedActionsMap = this.currentUserAllowedActions.reduce((acc, action) => ((acc[action] = true), acc), {});
    }
  }

  private offset = 0;

  createHeading(): void {
    const zIndex = this.whiteboardStateHolder.calculateNextZIndex();
    const shape: IHeadingState = createNewHeading(this.defaultPosition(), getCurrentUserId(), defaultHeadingFontFamily, zIndex);
    this.shapeCreate({ shape });
  }

  createNote(): void {
    const zIndex = this.whiteboardStateHolder.calculateNextZIndex();
    const shape: INoteState = createNewNote(this.defaultPosition(), getCurrentUserId(), zIndex);
    this.shapeCreate({ shape });
  }

  createBasicShape(shapeType: BasicShapeType): void {
    const zIndex = this.whiteboardStateHolder.calculateNextZIndex();
    const shape: IBasicShapeState = createBasicShape(shapeType, this.defaultPosition(), getCurrentUserId(), zIndex);
    this.shapeCreate({ shape });
  }

  uploadFromDevice(url: string, rawImageProps: IOriginalImageProps): IPromise<void> {
    const imageShapeId = uuidv4();
    return this.$rootScope.traceAction("upload_file_from_device", () => {
      return this.whiteboardImageService
        .uploadFromDevice(this.currentWhiteboardId, imageShapeId, url, rawImageProps)
        .then((imageUrl) => {
          const renderingSize = this.whiteboardImageService.calculateRenderingSize(rawImageProps);
          const rawImage = {
            name: rawImageProps.name,
            size: rawImageProps.size,
            type: rawImageProps.type,
            width: rawImageProps.width,
            height: rawImageProps.height,
          };
          const zIndex = this.whiteboardStateHolder.calculateNextZIndex();
          const shape = createNewImage({
            position: this.defaultPosition(),
            createdById: getCurrentUserId(),
            imageShapeId,
            imageUrl,
            renderingSize,
            rawImage,
            zIndex,
            rotationAngle: 0,
          });

          this.analyticsService.track("Whiteboard Image Uploaded", { upload_type: "device" });
          this.shapeCreate({ shape });
        })
        .catch(() => this.uiErrorHandlingService.handleModal({ body: localize("something_went_wrong") }));
    });
  }

  uploadViaUrl(): void {
    return this.$rootScope.traceAction("upload_file_via_url", () => {
      document.body.style.cursor = "progress";
      this.whiteboardModalService.getImage().then((imageUrl) => {
        if (!imageUrl) {
          return;
        }

        this.whiteboardImageService
          .imageSize(imageUrl)
          .then(
            (rawImageProps) => {
              if (rawImageProps) {
                const imageShapeId = uuidv4();
                const renderingSize = this.whiteboardImageService.calculateRenderingSize(rawImageProps);

                const imageRequestPayload = createImageRequestPayload(imageUrl, this.currentWhiteboardId);

                this.whiteboardImageService
                  .getExternalImage(imageRequestPayload)
                  .then((image) => {
                    const rawImage = {
                      width: rawImageProps.width,
                      height: rawImageProps.height,
                    };
                    const zIndex = this.whiteboardStateHolder.calculateNextZIndex();

                    const shape: IImageState = createNewImage({
                      position: this.defaultPosition(),
                      createdById: getCurrentUserId(),
                      imageShapeId,
                      imageUrl: image.url,
                      renderingSize,
                      rawImage,
                      zIndex,
                      rotationAngle: 0,
                    });
                    this.analyticsService.track("Whiteboard Image Uploaded", { upload_type: "url" });
                    this.shapeCreate({ shape });
                  })
                  .catch((err) => {
                    this.uiErrorHandlingService.handleModal(err);
                  });
              }
            },
            () => this.uiErrorHandlingService.handleModal({ body: localize("no_valid_image_url") })
          )
          .finally(() => (document.body.style.cursor = "auto"));
      }, noop);
    });
  }

  createGoal(): IPromise<void> {
    return this.getDefaultSessionId().then((defaultSessionId) => {
      const shape = this.shapeStateConverter.createDraftGoal("", { sessionId: defaultSessionId });
      shape.zIndex = this.whiteboardStateHolder.calculateNextZIndex();
      shape.position = this.defaultPosition();
      this.shapeCreate({ shape });
    });
  }

  importGoal(): void {
    this.goalModalsService.selectGoal({ invokedBy: "Whiteboards", showFutureSessions: true, showArchivedSessions: true }).then((goal) => {
      const shape = this.shapeStateConverter.goalToState(goal);
      shape.zIndex = this.whiteboardStateHolder.calculateNextZIndex();
      shape.position = this.defaultPosition();
      this.shapeCreate({ shape });
    }, noop);
  }

  private defaultPosition(): IPosition {
    const pos = this.viewport();
    const offset = this.offset; // this is to avoid offset on the first shape
    this.offset += 20;
    return {
      x: pos.x + viewportMargin.leftRight + offset,
      y: pos.y + viewportMargin.topBottom + offset,
    };
  }
}

export const WhiteboardMenuComponent: IComponentOptions = {
  templateUrl: require("./whiteboard-menu.jade"),
  controller: WhiteboardMenuComponentCtrl,
  bindings: {
    viewport: "<",
    reactionSettings: "<",
    currentUserAllowedActions: "<",
    getDefaultSessionId: "&",
    shapeCreate: "&",
  },
};
